// Beat 5 visual: a single SVG asset (`ASSETS.beat5Network` /
// `ASSETS.beat5NetworkTall`) contains the laptop + 8 device icons + the
// connecting lines. An inline SVG overlay redraws those same line
// geometries with bidirectional animated flashes, masked so the moving
// glow never slides across the central laptop image. A live `@daameer`
// Twitch link is overlaid as a real <a> so attribution stays clickable
// and trackable.
//
// Two layout variants:
//   - WIDE  (1600x1200, 4:3) — used on tablet/desktop.
//   - TALL  (1200x1600, 3:4) — used on mobile so the laptop has room.

import React from 'react';

import { ASSETS } from '@airhead-games/shared/configs';

type FlashLine = {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Cycle duration (s) for one end-to-end traversal. */
  duration: number;
  /** Phase offset (0–1) so flashes don't all fire in lockstep. */
  phase: number;
};

type Variant = {
  src: string;
  viewBox: { width: number; height: number };
  /** Central image rect inside the asset's viewBox. Same 588x326 in both
   *  variants, only the (x, y) origin differs. */
  laptopRect: { x: number; y: number; width: number; height: number };
  flashLines: FlashLine[];
};

const WIDE_VARIANT: Variant = {
  src: ASSETS.beat5Network,
  viewBox: { width: 1600, height: 1200 },
  laptopRect: { x: 506, y: 437, width: 588, height: 326 },
  flashLines: [
    {
      id: 'horizontal',
      x1: 357,
      y1: 596,
      x2: 1209,
      y2: 596,
      duration: 2.4,
      phase: 0.0,
    },
    {
      id: 'vertical',
      x1: 796,
      y1: 300,
      x2: 796,
      y2: 900,
      duration: 2.7,
      phase: 0.35,
    },
    {
      id: 'diag-tlbr',
      x1: 320,
      y1: 327,
      x2: 1325,
      y2: 907,
      duration: 2.2,
      phase: 0.6,
    },
    {
      id: 'diag-bltr',
      x1: 320,
      y1: 872,
      x2: 1276,
      y2: 321,
      duration: 2.55,
      phase: 0.2,
    },
  ],
};

const TALL_VARIANT: Variant = {
  src: ASSETS.beat5NetworkTall,
  viewBox: { width: 1200, height: 1600 },
  laptopRect: { x: 306, y: 637, width: 588, height: 326 },
  flashLines: [
    {
      id: 'horizontal',
      x1: 212,
      y1: 796,
      x2: 972,
      y2: 796,
      duration: 2.4,
      phase: 0.0,
    },
    {
      id: 'vertical',
      x1: 596,
      y1: 288,
      x2: 596,
      y2: 1339,
      duration: 2.7,
      phase: 0.35,
    },
    {
      id: 'diag-tlbr',
      x1: 211,
      y1: 407,
      x2: 1031,
      y2: 1227,
      duration: 2.2,
      phase: 0.6,
    },
    {
      id: 'diag-bltr',
      x1: 207,
      y1: 1187,
      x2: 988,
      y2: 407,
      duration: 2.55,
      phase: 0.2,
    },
  ],
};

/** Below this viewport width we serve the tall (portrait) variant.
 *  Matches Tailwind's `sm` breakpoint — anything below `sm:` is treated
 *  as mobile, which is where the wide layout was feeling cramped. */
const TALL_VARIANT_BREAKPOINT_PX = 640;

const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mql.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

/** Track viewport width so we can swap between wide/tall variants. */
const useViewportWidth = (): number => {
  const [width, setWidth] = React.useState(() =>
    typeof window === 'undefined' ? 0 : window.innerWidth
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setWidth(window.innerWidth);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return width;
};

type ContentRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

/** Measure the wrapper and compute the letterboxed content rect that the
 *  SVG renders into (via `object-contain`). Returns the rect in px
 *  relative to the wrapper, so the link can be positioned in actual
 *  rendered SVG coords rather than wrapper percentages — which only line
 *  up when the wrapper itself matches the asset aspect exactly. */
const useContentRect = (
  ref: React.RefObject<HTMLDivElement | null>,
  assetAspectWOverH: number
): ContentRect => {
  const [rect, setRect] = React.useState<ContentRect>({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });
  React.useLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const update = (containerW: number, containerH: number) => {
      if (containerW <= 0 || containerH <= 0) return;
      const containerAspect = containerW / containerH;
      let width: number;
      let height: number;
      if (containerAspect > assetAspectWOverH) {
        // Container wider than asset → content height-bound.
        height = containerH;
        width = height * assetAspectWOverH;
      } else {
        width = containerW;
        height = width / assetAspectWOverH;
      }
      setRect({
        left: (containerW - width) / 2,
        top: (containerH - height) / 2,
        width,
        height,
      });
    };
    const initialBox = node.getBoundingClientRect();
    update(initialBox.width, initialBox.height);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      update(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, assetAspectWOverH]);
  return rect;
};

const BeatFiveDeviceNetwork: React.FC = () => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const viewportWidth = useViewportWidth();

  const variant: Variant =
    viewportWidth > 0 && viewportWidth < TALL_VARIANT_BREAKPOINT_PX
      ? TALL_VARIANT
      : WIDE_VARIANT;

  const assetAspect = variant.viewBox.width / variant.viewBox.height;
  const contentRect = useContentRect(wrapperRef, assetAspect);

  /** Fraction (0–1) down from the top of the asset where the central
   *  image rect's bottom edge sits. */
  const imageRectBottomFraction =
    (variant.laptopRect.y + variant.laptopRect.height) / variant.viewBox.height;

  // Link size + inset both derived from the content rect width so they
  // scale 1:1 with the visual instead of with the wrapper.
  const linkFontSizePx =
    contentRect.width > 0
      ? Math.max(12, Math.min(24, contentRect.width * 0.022))
      : 14;
  const linkInsetPx = linkFontSizePx * 0.6;
  const linkBottomPx =
    contentRect.height > 0
      ? contentRect.top +
        contentRect.height * imageRectBottomFraction -
        linkInsetPx
      : 0;
  const linkLeftPx = contentRect.left + contentRect.width / 2;

  return (
    <div
      ref={wrapperRef}
      className={[
        'relative mx-auto h-full w-full',
        variant === WIDE_VARIANT
          ? 'max-w-[28rem] sm:max-w-xl md:max-w-2xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl min-[1920px]:max-w-6xl'
          : 'max-w-[22rem]',
      ].join(' ')}
      style={{
        aspectRatio: `${variant.viewBox.width} / ${variant.viewBox.height}`,
      }}
    >
      <img
        src={variant.src}
        alt=""
        className="absolute inset-0 h-full w-full object-contain"
        loading="lazy"
        draggable={false}
      />

      {!reducedMotion && (
        <svg
          viewBox={`0 0 ${variant.viewBox.width} ${variant.viewBox.height}`}
          className="pointer-events-none absolute inset-0 h-full w-full"
          aria-hidden
        >
          <defs>
            <mask id="beat5-laptop-mask">
              <rect
                width={variant.viewBox.width}
                height={variant.viewBox.height}
                fill="white"
              />
              <rect
                x={variant.laptopRect.x}
                y={variant.laptopRect.y}
                width={variant.laptopRect.width}
                height={variant.laptopRect.height}
                rx="10"
                fill="black"
              />
            </mask>
          </defs>
          <g mask="url(#beat5-laptop-mask)">
            {variant.flashLines.map((line) => {
              const dashOn = 8;
              const dashOff = 92;
              const delayOut = -line.duration * line.phase;
              const delayIn = -line.duration * ((line.phase + 0.5) % 1);
              return (
                <g key={line.id}>
                  <line
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke="#E5FFEB"
                    strokeWidth={14}
                    strokeLinecap="round"
                    pathLength={100}
                    strokeDasharray={`${dashOn} ${dashOff}`}
                    style={{
                      animation: `beat5-flash-out ${line.duration}s linear ${delayOut}s infinite`,
                    }}
                  />
                  <line
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke="#A8F5C5"
                    strokeWidth={12}
                    strokeLinecap="round"
                    pathLength={100}
                    strokeDasharray={`${dashOn} ${dashOff}`}
                    style={{
                      animation: `beat5-flash-in ${line.duration}s linear ${delayIn}s infinite`,
                    }}
                  />
                </g>
              );
            })}
          </g>
        </svg>
      )}

      <a
        href="https://twitch.tv/daameer"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute font-semibold tracking-wide text-white underline-offset-4 hover:underline"
        style={{
          left: `${linkLeftPx}px`,
          top: `${linkBottomPx}px`,
          transform: 'translate(-50%, -100%)',
          fontSize: `${linkFontSizePx}px`,
          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
        }}
      >
        @daameer
      </a>

      <style>{`
        @keyframes beat5-flash-out {
          0%   { stroke-dashoffset: 100; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes beat5-flash-in {
          0%   { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: 100; }
        }
      `}</style>
    </div>
  );
};

export default BeatFiveDeviceNetwork;
